import { useTranslation } from 'next-i18next'

import { useRef, useState } from 'react'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import Equals from '~/bootcamp/Activities/components/Equals'
import Input from '~/bootcamp/Activities/components/Input'
import Times from '~/bootcamp/Activities/components/Times'
import useFocus from '~/bootcamp/Activities/hooks/useFocus'
import { hocOnEnterPress } from '~/bootcamp/Activities/utils/activities.utils'

import { PRIME_NUMBERS } from '~/bootcamp/Activities/Factorization/constants'

import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

import { useFactorNegative } from './useFactorNegative'

const initialFactors = [
  { factor: 1, answer: '' },
  { factor: 2, answer: '' },
  { factor: 3, answer: '' },
  { factor: 4, answer: '' },
  { factor: 5, answer: '' },
]

const FactorNegative = ({
  answerState,
  onAnswerStateChange,
  onInstanceChange,
  onNextQuestionClick,
  actionButtonsFC: SubmitButton,
  difficultyLevel,
  autofocus = true,
}: ActivityProps): React.ReactElement => {
  const { t } = useTranslation('generics')
  const [factorNegativeQuestion, generateNewQuestion] = useFactorNegative(difficultyLevel)
  const [factors, setFactors] = useState(initialFactors)
  const [hasCheckedAnswer, setHasCheckedAnswer] = useState(false)

  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, factorNegativeQuestion.factors.length ? inputRef : { current: null })

  useEmitInstance(
    onInstanceChange,
    factorNegativeQuestion.factors.length
      ? `${factorNegativeQuestion.sum} = ${Array(factorNegativeQuestion.factors.length)
          .fill('\\square')
          .join(' \\cdot ')}`
      : null,
  )

  const handleOnChange = (value: string, index: number): void => {
    setHasCheckedAnswer(false)
    setFactors(factors.map((factor, i) => (i === index ? { ...factor, answer: value } : { ...factor })))
  }

  const isValid = (): boolean => {
    const validate = factors.map((factor) => {
      if (factor.factor > factorNegativeQuestion.factors.length) return true
      else if (factor.answer === '') return false
      else return true
    })
    return !validate.includes(false)
  }

  const handleOnCheckAnswerClick = (): boolean => {
    let sum = 1
    let primeNum = true

    setHasCheckedAnswer(true)
    ;[...Array(factorNegativeQuestion.factors.length)].map((_e, i) => {
      if (!PRIME_NUMBERS.has(parseFloat(factors[i].answer)) && factors[i].answer !== '-1') {
        primeNum = false
      }
      return (sum *= parseFloat(factors[i].answer))
    })
    const check = primeNum ? factorNegativeQuestion.sum === sum : false
    onAnswerStateChange(check)
    return check
  }

  const handleOnNextQuestionClick = (): void => {
    generateNewQuestion()
    onNextQuestionClick()
    reset()
  }

  const reset = (): void => {
    setFactors(initialFactors)
    setHasCheckedAnswer(false)
    onAnswerStateChange(null)
  }

  const onEnterPress = (): void => {
    if (isValid()) {
      hocOnEnterPress(answerState, handleOnCheckAnswerClick, handleOnNextQuestionClick)
    }
  }

  return (
    <ActivityWrapper className="justify-between">
      <div className="flex flex-1 space-x-1 items-center text-purple font-semibold text-lg">
        <p>{factorNegativeQuestion.sum}</p>
        <Equals />
        {factorNegativeQuestion.factors.map((_factor, i) => (
          <div key={`factor${i.toString()}`}>
            <Input
              aria-label={t('ariaLabelButtons.insertFactor')}
              autoFocus={i === 0}
              isError={hasCheckedAnswer && !factorNegativeQuestion.factors.includes(parseFloat(factors[i].answer))}
              key={`input${i.toString()}`}
              maxDigits={4}
              onChange={(value) => handleOnChange(value, i)}
              onEnter={onEnterPress}
              ref={i === 0 ? inputRef : null}
              type="number"
              value={factors[i].answer}
            />
            {factorNegativeQuestion.factors.length > i + 1 ? <Times key={`times${i.toString()}`} /> : <></>}
          </div>
        ))}
      </div>
      {SubmitButton && (
        <SubmitButton
          clearFormAction={reset}
          correctAnswer={!!answerState}
          formValid={isValid()}
          goToNextQuestionAction={handleOnNextQuestionClick}
          submitAnswerAction={handleOnCheckAnswerClick}
        />
      )}
    </ActivityWrapper>
  )
}

export default FactorNegative
